import { Alert, Card, Col, Container, Row } from 'react-bootstrap'
import { Link } from 'react-router-dom'
import {
  AlertTriangle,
  BookOpen,
  ClipboardList,
  GitCompareArrows,
  PlayCircle,
  ShoppingBag,
} from 'lucide-react'

const spikeUi = import.meta.env.VITE_COMPLIANCE_SPIKE_UI === 'true'

import { defaultWorkflow } from '../workflow/defaultWorkflow'
import { getHubBodyForStep } from '../workflow/workflowHubContent'

export default function ComplianceAuditHub() {
  return (
    <Container className="py-4">
      <div className="d-flex align-items-start gap-3 mb-4">
        <div className="rounded-circle bg-primary bg-opacity-10 p-3 text-primary">
          <ClipboardList size={28} aria-hidden />
        </div>
        <div>
          <h1 className="h3 mb-1">Compliance audit</h1>
          <p className="text-muted mb-0 small">
            Periodic audit workflow: follow the same seven steps below as <strong>Compliance setup</strong>—then revisit findings after runs finish.
          </p>
        </div>
      </div>

      {!spikeUi && (
        <Alert variant="warning" className="mb-4">
          Build with <code className="small">VITE_COMPLIANCE_SPIKE_UI=true</code> for shortcuts to comparison scope from the product library (row actions and sidebar).
          Backend flags <code className="small">ComplianceSpike__ScopedExtraction</code> and{' '}
          <code className="small">ComplianceSpike__TerminologyScanAfterDistributorScrape</code> still apply independently.
        </Alert>
      )}

      <Card className="mb-4 shadow-sm border-primary-subtle">
        <Card.Body>
          <div className="d-flex align-items-center gap-2 mb-3 text-primary">
            <BookOpen size={22} aria-hidden />
            <Card.Title className="h6 mb-0">Compliance workflow — seven steps (in order)</Card.Title>
          </div>
          <ol className="small mb-3 ps-3">
            {defaultWorkflow.steps.map((s) => (
              <li key={s.key} className="mb-2">
                <strong>{s.title}.</strong> {getHubBodyForStep(s.key) ?? s.description}
              </li>
            ))}
          </ol>
          <details className="small border rounded px-3 py-2 bg-light bg-opacity-50">
            <summary className="fw-semibold" style={{ cursor: 'pointer' }}>
              Manual smoke path (QA)
            </summary>
            <ol className="mt-2 mb-0 ps-3 text-muted">
              <li className="mb-1">
                Complete steps 1–2: <Link to="/master-terms">master terms</Link> then <Link to="/pim">/pim</Link> — family, subset, import or add compared attributes.
              </li>
              <li className="mb-1">
                Steps 3–4: Seed or verify <Link to="/products">products</Link> and an approved <Link to="/rules">rule pack</Link>.
              </li>
              <li className="mb-1">
                Step 5: <Link to="/distributors">Distributor</Link> plus <Link to="/targets">targets</Link> for scrape URLs.
              </li>
              <li className="mb-1">
                Steps 6–7: Queue from <Link to="/run-checks">run checks</Link>, confirm a <Link to="/check-runs">job</Link>, review <Link to="/findings">findings</Link> / <Link to="/distributor-action-reports">reports</Link> (environment-dependent).
              </li>
            </ol>
          </details>
        </Card.Body>
      </Card>

      <Row className="g-3">
        <Col md={6} lg={4}>
          <Card className="h-100 shadow-sm">
            <Card.Body className="d-flex flex-column">
              <div className="d-flex align-items-center gap-2 mb-2 text-primary">
                <ShoppingBag size={22} aria-hidden />
                <Card.Title className="h6 mb-0">Products &amp; scope</Card.Title>
              </div>
              <Card.Text className="small text-muted flex-grow-1">
                Open the dedicated comparison scope page for a product, or use the audit scope card when viewing product details.
              </Card.Text>
              <Link className="btn btn-primary btn-sm" to="/products">
                Open product library
              </Link>
              {spikeUi && (
                <p className="small text-muted mb-0 mt-2">
                  Tip: use the <strong>clipboard</strong> icon in the register row actions, or{' '}
                  <strong>Comparison scope</strong> in the header when the sidebar is open.
                </p>
              )}
              {!spikeUi && (
                <p className="small text-muted mb-0 mt-2">
                  Direct URL pattern: <code className="small">/products/&lt;productId&gt;/compliance-scope</code>
                </p>
              )}
            </Card.Body>
          </Card>
        </Col>
        <Col md={6} lg={4}>
          <Card className="h-100 shadow-sm">
            <Card.Body className="d-flex flex-column">
              <div className="d-flex align-items-center gap-2 mb-2 text-primary">
                <GitCompareArrows size={22} aria-hidden />
                <Card.Title className="h6 mb-0">Compare</Card.Title>
              </div>
              <Card.Text className="small text-muted flex-grow-1">
                Side-by-side Kingspan vs distributor values. Use filters for diffs or missing fields.
              </Card.Text>
              <Link className="btn btn-outline-primary btn-sm" to="/products">
                Choose product, then Compare
              </Link>
              <span className="small text-muted mt-2">
                From the library row actions, open <strong>Compare</strong> for a product.
              </span>
            </Card.Body>
          </Card>
        </Col>
        <Col md={6} lg={4}>
          <Card className="h-100 shadow-sm">
            <Card.Body className="d-flex flex-column">
              <div className="d-flex align-items-center gap-2 mb-2 text-primary">
                <AlertTriangle size={22} aria-hidden />
                <Card.Title className="h6 mb-0">Findings</Card.Title>
              </div>
              <Card.Text className="small text-muted flex-grow-1">
                <strong>Value</strong> issues vs <strong>Terminology</strong> rule hits are labelled by finding type.
              </Card.Text>
              <Link className="btn btn-outline-primary btn-sm" to="/findings">
                Open findings
              </Link>
            </Card.Body>
          </Card>
        </Col>
        <Col md={6} lg={4}>
          <Card className="h-100 shadow-sm">
            <Card.Body className="d-flex flex-column">
              <div className="d-flex align-items-center gap-2 mb-2 text-primary">
                <PlayCircle size={22} aria-hidden />
                <Card.Title className="h6 mb-0">Run checks</Card.Title>
              </div>
              <Card.Text className="small text-muted flex-grow-1">
                Queue scans and automated comparison jobs for approved products.
              </Card.Text>
              <Link className="btn btn-outline-primary btn-sm" to="/run-checks">
                Run checks
              </Link>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  )
}
